<template>
	<v-container grid-list-md id="reservation-widget">
		<v-card max-width="550px" class="mx-auto">
			<v-img
				v-if="tenant.cover_image_url"
				:src="tenant.cover_image_url"
				height="100"
				width="100%"
			></v-img>
			<v-list-item class="my-0 py-0" style="height: 60px">
				<v-list-item-action>
					<v-img
						:src="tenant.image_url"
						width="60"
						height="60"
						contain
					></v-img>
				</v-list-item-action>
				<v-list-item-content>
					<v-list-item-title
						v-if="current_outlet"
						style="font-size: 20px; font-weight: bold"
					>
						{{ current_outlet.name }}
					</v-list-item-title>
				</v-list-item-content>
				<v-list-item-action>
					<v-select
						style="width: 100px"
						dense
						single-line
						hide-details
						v-model="locale"
						:items="locales"
						@change="changeLocale"
						item-value="value"
						item-text="name"
					>
					</v-select>
				</v-list-item-action>
			</v-list-item>
			<v-divider></v-divider>
			<v-card-text class="pa-0">
				<v-alert
					type="success"
					v-if="success"
					color="secondary"
					dismissible
					dense
					><span v-html="success"></span
				></v-alert>
				<v-alert type="error" v-if="error" dismissible dense
					><span v-html="error"></span
				></v-alert>
				<v-stepper v-model="step" :elevation="0">
					<v-stepper-header :elevation="0" style="height: 40px">
						<v-stepper-step
							:complete="step > 1"
							step="1"
							style="padding: 8px 24px"
						>
							{{ $__("Reservation") }}
						</v-stepper-step>

						<v-divider></v-divider>

						<v-stepper-step
							:complete="step > 2"
							step="2"
							style="padding: 8px 24px"
						>
							{{ $__("Information") }}
						</v-stepper-step>

						<v-divider></v-divider>

						<v-stepper-step step="3" style="padding: 8px 24px">
							{{ $__("Confirmation") }}
						</v-stepper-step>
					</v-stepper-header>

					<v-stepper-items>
						<v-stepper-content step="1">
							<ValidationObserver
								ref="obs"
								v-slot="{ invalid }"
								:key="reservationKey"
							>
								<v-card
									class="mb-2"
									color="blue-grey lighten-5"
								>
									<v-card-text>
										<v-layout row wrap>
											<v-flex
												xs12
												v-show="outlets.length > 1"
											>
												<ValidationProvider
													name="outlet_uuid"
													rules="required"
													v-slot="{ errors }"
												>
													<v-select
														:items="outlets"
														v-model="
															item.outlet_uuid
														"
														item-value="uuid"
														item-text="name"
														:label="
															$__(
																'Select Location'
															)
														"
														prepend-inner-icon="mdi-store"
														dense
														:error-messages="errors"
														class="required"
													>
													</v-select>
												</ValidationProvider>
											</v-flex>
											<v-flex xs12 md6>
												<ValidationProvider
													name="adults"
													rules="required"
													v-slot="{ errors }"
												>
													<v-select
														:items="adultsList"
														v-model="item.adults"
														:label="$__('Adults')"
														prepend-inner-icon="mdi-account-multiple"
														dense
														:error-messages="errors"
														class="required"
													>
													</v-select>
												</ValidationProvider>
											</v-flex>
											<v-flex xs12 md6>
												<ValidationProvider
													name="children"
													rules="required"
													v-slot="{ errors }"
												>
													<v-select
														:items="childrenList"
														v-model="item.children"
														prepend-inner-icon="mdi-baby"
														:label="$__('Children')"
														dense
														:error-messages="errors"
														class="required"
													>
													</v-select>
												</ValidationProvider>
											</v-flex>
											<v-flex xs12 md6>
												<v-menu
													v-model="date"
													:close-on-content-click="
														false
													"
													max-width="290"
												>
													<template
														v-slot:activator="{
															on,
															attrs,
														}"
													>
														<ValidationProvider
															name="date"
															rules="required"
															v-slot="{ errors }"
														>
															<v-text-field
																:value="
																	formattedDate
																"
																:label="
																	$__('Date')
																"
																readonly
																v-bind="attrs"
																v-on="on"
																@click:clear="
																	item.date =
																		null
																"
																prepend-inner-icon="mdi-calendar"
																data-vv-name="date"
																class="required"
																dense
																:error-messages="
																	errors
																"
															></v-text-field>
														</ValidationProvider>
													</template>
													<v-date-picker
														v-model="item.date"
														@change="date = false"
														:min="today"
														color="primary"
													></v-date-picker>
												</v-menu>
											</v-flex>
											<v-flex xs12 md6>
												<ValidationProvider
													name="time"
													rules="required"
													v-slot="{ errors }"
												>
													<v-select
														:label="$__('Time')"
														:items="times"
														v-model="item.time"
														:return-object="false"
														prepend-inner-icon="mdi-clock"
														data-vv-name="reservation.time"
														class="required"
														:loading="loading"
														loader-height="1"
														dense
														:error-messages="errors"
													>
													</v-select>
												</ValidationProvider>
											</v-flex>
											<v-flex xs12 md12>
												<v-textarea
													:label="$__('Comment')"
													prepend-inner-icon="mdi-comment"
													v-model="item.description"
													rows="2"
													data-vv-name="description"
												>
												</v-textarea>
											</v-flex>
										</v-layout>
									</v-card-text>
								</v-card>

								<v-btn
									color="primary"
									block
									@click="step = 2"
									:disabled="invalid"
								>
									{{ $__("Next") }}
								</v-btn>
							</ValidationObserver>
						</v-stepper-content>

						<v-stepper-content step="2">
							<ValidationObserver
								ref="obs"
								v-slot="{ invalid }"
								:key="informationKey"
							>
								<v-card
									class="mb-2"
									color="blue-grey lighten-5"
								>
									<v-card-text>
										<v-layout row wrap>
											<v-flex xs12>
												<v-alert
													v-if="current_outlet"
													dense
												>
													We have a table for you at
													{{ current_outlet.name }}
													for
													{{
														item.adults +
														item.children
													}}
													people at
													{{ formattedTime }}
													on
													{{ formattedDate }}.
												</v-alert>
											</v-flex>
											<v-flex xs12 md6>
												<ValidationProvider
													name="first_name"
													rules="required"
													v-slot="{ errors }"
												>
													<v-text-field
														v-model="
															item.first_name
														"
														:label="
															$__('First Name')
														"
														prepend-inner-icon="mdi-account"
														dense
														:error-messages="errors"
														class="required"
													>
													</v-text-field>
												</ValidationProvider>
											</v-flex>
											<v-flex xs12 md6>
												<ValidationProvider
													name="last_name"
													rules="required"
													v-slot="{ errors }"
												>
													<v-text-field
														v-model="item.last_name"
														:label="
															$__('Last Name')
														"
														prepend-inner-icon="mdi-account"
														dense
														:error-messages="errors"
														class="required"
													>
													</v-text-field>
												</ValidationProvider>
											</v-flex>
											<v-flex xs12 md12>
												<ValidationProvider
													name="email"
													rules="required|email"
													v-slot="{ errors }"
												>
													<v-text-field
														v-model="item.email"
														:label="$__('Email')"
														prepend-inner-icon="mdi-email"
														dense
														:error-messages="errors"
														class="required"
													>
													</v-text-field>
												</ValidationProvider>
											</v-flex>
											<v-flex xs12 md12>
												<ValidationProvider
													name="phone"
													rules="required|phone"
													v-slot="{ errors }"
												>
													<vue-tel-input-vuetify
														v-model="item.phone"
														mode="international"
														:preferredCountries="[
															'KH',
														]"
														:placeholder="null"
														:label="
															$__('Phone Number')
														"
														validate-on-blur
														required
														:error-messages="errors"
														class="required"
													></vue-tel-input-vuetify>
												</ValidationProvider>
											</v-flex>
										</v-layout>
									</v-card-text>
									<v-card-actions>
										<v-btn text @click="step = 1">
											{{ $__("Back") }}
										</v-btn>
										<v-spacer></v-spacer>

										<v-btn
											color="primary"
											@click="submit()"
											:disabled="invalid"
											:loading="loading"
										>
											{{ $__("Submit Reservation") }}
										</v-btn>
									</v-card-actions>
								</v-card>
							</ValidationObserver>
						</v-stepper-content>

						<v-stepper-content step="3">
							<v-card class="mb-2" color="blue-grey lighten-5">
								<v-card-text>
									<v-layout row wrap>
										<v-flex xs12>
											<v-alert
												type="success"
												color="secondary"
												dark
												dense
												border="left"
												class="mb-0"
												>Your Reservation is
												{{ reservation.status }}!
												Reservation details have been
												sent to {{ reservation.email }}.
											</v-alert>
										</v-flex>
										<v-flex xs12>
											<v-list>
												<v-list-item>
													<v-list-item-action>
														<v-icon
															>mdi-qrcode</v-icon
														>
													</v-list-item-action>
													<v-list-item-content>
														<v-list-item-title>
															{{ $__("Code") }}:
															#{{
																reservation.code
															}}
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item>
													<v-list-item-action>
														<v-icon
															>mdi-account</v-icon
														>
													</v-list-item-action>
													<v-list-item-content>
														<v-list-item-title>
															{{
																reservation.name
															}}
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item>
													<v-list-item-action>
														<v-icon
															>mdi-calendar</v-icon
														>
													</v-list-item-action>
													<v-list-item-content>
														<v-list-item-title>
															{{
																reservation.formatted_date
															}}
															@{{
																reservation.formatted_time
															}}
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item>
													<v-list-item-action>
														<v-icon
															>mdi-account-group</v-icon
														>
													</v-list-item-action>
													<v-list-item-content>
														<v-list-item-title>
															{{
																reservation.adults
															}}
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item>
													<v-list-item-action>
														<v-icon
															>mdi-baby</v-icon
														>
													</v-list-item-action>
													<v-list-item-content>
														<v-list-item-title>
															{{
																reservation.children
															}}
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item>
													<v-list-item-action>
														<v-icon
															>mdi-comment</v-icon
														>
													</v-list-item-action>
													<v-list-item-content>
														<v-list-item-title>
															{{
																reservation.description
															}}
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
											</v-list>
										</v-flex>
									</v-layout>
								</v-card-text>
							</v-card>

							<v-spacer></v-spacer>
							<v-btn
								color="primary"
								block
								@click="newReservation()"
							>
								{{ $__("New Reservation") }}
							</v-btn>
						</v-stepper-content>
					</v-stepper-items>
				</v-stepper>
			</v-card-text>
			<v-card-actions class="text-center">
				<v-spacer></v-spacer>
				<p class="text-center">
					Reservation powered by Thalias
					<br />
					© 2022 Thalias, All right reserved. <br />
					By clicking “Submit reservation” you agree to the
					<a :href="terms_of_use_url" :target="'blank'"
						>Thalias Terms of Use</a
					>
					and
					<a :href="privacy_policy_url" :target="'blank'"
						>Privacy Policy.</a
					>
				</p>
				<v-spacer></v-spacer>
			</v-card-actions>
		</v-card>
	</v-container>
</template>

<script>
import { format, parseISO } from "date-fns";
import axios from "axios";
export default {
	props: [
		"tenant",
		"outlets",
		"outlet",
		"adults",
		"children",
		"terms_of_use_url",
		"privacy_policy_url",
		"outlet_uuid",
	],
	data() {
		return {
			step: 1,
			today: format(parseISO(new Date().toISOString()), "yyyy-MM-dd"),
			date: false,
			locale: this.$locale(),
			item: {
				adults: 1,
				children: 0,
				title: null,
				first_name: null,
				last_name: null,
				email: null,
				phone: null,
				outlet_uuid: this.outlet?.uuid,
			},
			current_outlet: this.outlet,
			times: [],
			loading: false,
			reservation: {},
			reservationKey: 0,
			informationKey: 0,
			success: null,
			error: null,
			adultsList: this.adults,
			childrenList: this.children,
		};
	},

	computed: {
		formattedDate() {
			return this.item.date
				? format(parseISO(this.item.date), "EEEE, MMMM do yyyy")
				: "";
		},

		formattedTime() {
			return this.item.time && this.item.date
				? format(
						parseISO(this.item.date + " " + this.item.time),
						"h:mm a"
				  )
				: "";
		},

		locales() {
			return [
				{
					value: "en",
					name: "English",
				},
				{
					value: "km",
					name: "ភាសាខ្មែរ",
				},
				{
					value: "fr",
					name: "Français",
				},
				{
					value: "zh",
					name: "中文",
				},
			];
		},
	},

	watch: {
		"item.date": {
			handler() {
				this.getTimes();
			},
		},
		"item.outlet_uuid": {
			handler() {
				this.getTimes();
				this.getMax();
				this.current_outlet = this.outlets.find(
					(outlet) => outlet.uuid == this.item.outlet_uuid
				);
			},
		},
	},

	methods: {
		getTimes() {
			try {
				if (this.item.date && this.item.outlet_uuid) {
					this.loading = true;
					axios
						.get(
							`/widget/reservation/${this.item.outlet_uuid}/times`,
							{
								params: {
									date: this.item.date,
								},
							}
						)
						.then((response) => {
							if (response.data.success) {
								this.times = response.data.data;
							} else {
								this.success = null;
								this.error = response.data.data;
							}
						})
						.catch((error) => {
							this.success = null;
							this.error = error;
						})
						.finally(() => {
							this.loading = false;
						});
				}
			} catch (error) {
				this.success = null;
				this.error = error;
			}
		},

		getMax() {
			try {
				if (this.item.outlet_uuid) {
					this.loading = true;
					axios
						.get(`/widget/reservation/max`, {
							params: {
								outlet_uuid: this.item.outlet_uuid,
							},
						})
						.then((response) => {
							if (response.data.success) {
								this.adultsList = response.data.data.adults;
								this.childrenList = response.data.data.children;
							} else {
								this.success = null;
								this.error = response.data.data;
							}
						})
						.catch((error) => {
							this.success = null;
							this.error = error;
						})
						.finally(() => {
							this.loading = false;
						});
				}
			} catch (error) {
				this.success = null;
				this.error = error;
			}
		},

		submit() {
			try {
				this.loading = true;
				axios
					.get("/widget/token")
					.then((response) => {
						if (response.status == 200) {
							axios
								.post(
									`/widget/reservation/${this.item.outlet_uuid}/store`,
									this.item,
									{
										headers: {
											"X-CSRF-TOKEN": response.data,
										},
									}
								)
								.then((response) => {
									if (response.data.success) {
										this.reservation = response.data.data;
										this.step = 3;
									} else {
										this.success = null;
										this.error = response.data.data;
									}
								})
								.catch((error) => {
									this.success = null;
									this.error = error;
								})
								.finally(() => {
									this.loading = false;
								});
						}
					})
					.catch((error) => {
						this.success = null;
						this.error = error;
					})
					.finally(() => {
						this.loading = false;
					});
			} catch (error) {
				this.success = null;
				this.error = error;
			}
		},

		changeLocale() {
			this.$setLocale(this.locale);
			this.$forceUpdate();
			this.reservationKey += 1;
			this.informationKey += 1;
		},

		newReservation() {
			this.item = Object.assign({}, this.item, {
				adults: 1,
				children: 0,
				date: null,
				time: null,
				description: null,
				title: null,
				first_name: null,
				last_name: null,
				email: null,
				phone: null,
				outlet_uuid: this.outlet?.uuid,
			});
			this.times = [];
			this.reservation = {};
			this.step = 1;
		},
	},
};
</script>